import React, { useState, useMemo, useEffect } from "react";
import styles from "./NotificationsModal.module.css";
import { Portal } from "../Portal/Portal";
import { useBackButton } from "../../hooks/useBackButton";
import { useNotificationsAutoLoad } from "../../hooks/useNotifications";
import { NotificationCategory, Notification } from "../../types/notifications";
import { NotificationDetailModal } from "./NotificationDetailModal/NotificationDetailModal";
import bellIcon from "../icons/notifications/bell.png";

interface NotificationsModalProps {
  onClose: () => void;
}

type FilterTab = "all" | "networking" | "dps";

export const NotificationsModal: React.FC<NotificationsModalProps> = ({
  onClose,
}) => {
  const [activeFilter, setActiveFilter] = useState<FilterTab>("all");
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);
  const {
    notifications: allNotifications,
    markAsRead,
    loading,
    syncInBackground,
  } = useNotificationsAutoLoad(true);

  useBackButton({
    isOpen: true,
    onBack: selectedNotification ? () => setSelectedNotification(null) : onClose,
  });

  const handleFilterClick = (filter: FilterTab) => {
    setActiveFilter(filter);
  };

  useEffect(() => {
    if (activeFilter === "networking") {
      syncInBackground({ category: NotificationCategory.NETWORKING });
    } else if (activeFilter === "dps") {
      syncInBackground({ category: NotificationCategory.DPS });
    } else {
      syncInBackground();
    }
  }, [activeFilter, syncInBackground]);

  const notifications = useMemo(() => {
    if (activeFilter === "all") {
      return allNotifications;
    } else if (activeFilter === "networking") {
      return allNotifications.filter(
        (n) => n.category === NotificationCategory.NETWORKING
      );
    } else if (activeFilter === "dps") {
      return allNotifications.filter(
        (n) => n.category === NotificationCategory.DPS
      );
    }
    return allNotifications;
  }, [activeFilter, allNotifications]);

  // Форматирование времени
  const formatTime = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp * 1000;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (hours < 24) {
      const date = new Date(timestamp * 1000);
      return `${String(date.getHours()).padStart(2, "0")}:${String(
        date.getMinutes()
      ).padStart(2, "0")}`;
    } else if (days === 1) {
      return "вчера";
    } else if (days < 7) {
      return `${days} дн. назад`;
    } else {
      const date = new Date(timestamp * 1000);
      const months = [
        "янв.",
        "фев.",
        "мар.",
        "апр.",
        "мая",
        "июн.",
        "июл.",
        "авг.",
        "сен.",
        "окт.",
        "ноя.",
        "дек.",
      ];
      return `${date.getDate()} ${months[date.getMonth()]}`;
    }
  };

  const networkingUnreadCount = allNotifications.filter(
    (n) => n.category === NotificationCategory.NETWORKING && !n.is_read
  ).length;

  const isEmpty = notifications.length === 0;
  const isInitialLoading = loading && allNotifications.length === 0;

  return (
    <>
      <Portal>
        <div className={styles.overlay}>
          <div className={styles.container}>
            {/* Заголовок */}
            <div className={styles.header}>
              <h1 className={styles.title}>Уведомления</h1>
            </div>

            {/* Табы фильтрации */}
            <div className={styles.filters}>
              <button
                className={`${styles.filterButton} ${
                  activeFilter === "all" ? styles.filterButtonActive : ""
                }`}
                onClick={() => handleFilterClick("all")}
              >
                <span className={styles.filterText}>Все</span>
              </button>
              <button
                className={`${styles.filterButton} ${
                  activeFilter === "networking"
                    ? styles.filterButtonActive
                    : ""
                }`}
                onClick={() => handleFilterClick("networking")}
              >
                <span className={styles.filterText}>Нетворкинг</span>
                {networkingUnreadCount > 0 && (
                  <div className={styles.filterBadge}>
                    <span className={styles.filterBadgeText}>
                      {networkingUnreadCount}
                    </span>
                  </div>
                )}
              </button>
              <button
                className={`${styles.filterButton} ${
                  activeFilter === "dps" ? styles.filterButtonActive : ""
                }`}
                onClick={() => handleFilterClick("dps")}
              >
                <span className={styles.filterText}>ДПС-Контроль</span>
              </button>
            </div>

            {/* Контент */}
            <div className={styles.content}>
              {isInitialLoading ? (
                // Скелетон при первой загрузке
                <div className={styles.notificationsList}>
                  {[1, 2, 3].map((i) => (
                    <div key={i} className={styles.notificationSkeleton}>
                      <div className={styles.skeletonHeader}>
                        <div className={styles.skeletonTitle} />
                        <div className={styles.skeletonTime} />
                      </div>
                      <div className={styles.skeletonMessage} />
                    </div>
                  ))}
                </div>
              ) : isEmpty ? (
                // Empty State
                <div className={styles.emptyState}>
                  <img
                    src={bellIcon}
                    alt="Нет уведомлений"
                    className={styles.emptyIcon}
                  />
                  <div className={styles.emptyText}>Нет новых уведомлений</div>
                </div>
              ) : (
                // Список уведомлений
                <div className={styles.notificationsList}>
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={styles.notificationItem}
                      data-new={!notification.is_read}
                      onClick={() => {
                        if (!notification.is_read) {
                          markAsRead(notification.id);
                        }
                        setSelectedNotification(notification);
                      }}
                    >
                      <div className={styles.notificationHeader}>
                        <div className={styles.notificationTitle}>
                          {notification.title}
                        </div>
                        <div className={styles.notificationTime}>
                          {!notification.is_read && (
                            <div className={styles.notificationDot} />
                          )}
                          <div className={styles.notificationTimeText}>
                            {formatTime(notification.created_at)}
                          </div>
                        </div>
                      </div>
                      <div className={styles.notificationMessage}>
                        {notification.message}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Portal>

      {selectedNotification && (
        <NotificationDetailModal
          notification={selectedNotification}
          onClose={() => setSelectedNotification(null)}
        />
      )}
    </>
  );
};
